import { normalizeDocumentCategory } from "./moveDocumentScope";

function sanitizePathSegment(value) {
  return String(value || "Business")
    .trim()
    .replace(/[/\\]+/g, "_");
}

export function getClientS3Root(clientData) {
  return (
    clientData?.s3_root_folder || clientData?.home_folder || "Home"
  );
}

/** Stable id for business dropdown + API client_business_id (falls back to name). */
export function getBusinessSelectionId(business) {
  if (!business) return "";
  if (business.client_business_id != null && business.client_business_id !== "") {
    return String(business.client_business_id);
  }
  return String(business.business_name || "").trim();
}

export function findBusinessBySelectionId(businesses, selectionId) {
  if (!selectionId) return null;
  const target = String(selectionId);
  return (businesses || []).find(
    (business) => getBusinessSelectionId(business) === target,
  );
}

/** Per-business S3 root: {client_root}/Business/{business_id_or_name} */
export function getBusinessDocumentRootFolder(clientS3Root, business) {
  const root = String(clientS3Root || "Home").replace(/\/$/, "");
  const segment = sanitizePathSegment(
    business?.client_business_id || business?.business_name,
  );
  return `${root}/Business/${segment}`;
}

/** Prefer owner S3 root when this client is an assigned member of a shared business. */
export function getEffectiveClientS3Root(clientData, business) {
  if (business?.owner_s3_root_folder) {
    return String(business.owner_s3_root_folder).replace(/\/$/, "");
  }
  return getClientS3Root(clientData);
}

/** Full home_folder for uploads / directory_details (business) or client root (individual). */
export function getDocumentHomeFolder({
  clientData,
  documentCategory,
  selectedBusiness,
} = {}) {
  const base = getEffectiveClientS3Root(clientData, selectedBusiness);
  if (normalizeDocumentCategory(documentCategory) !== "business") {
    return getClientS3Root(clientData);
  }
  if (!selectedBusiness) return base;
  return getBusinessDocumentRootFolder(base, selectedBusiness);
}

/** Add client_business_id when document_category is business. */
export function withBusinessDocumentParams(
  payload = {},
  { documentCategory, clientBusinessId } = {},
) {
  const category = normalizeDocumentCategory(
    documentCategory ?? payload?.document_category,
  );
  if (category !== "business") return payload;
  if (clientBusinessId == null || clientBusinessId === "") return payload;
  return { ...payload, client_business_id: clientBusinessId };
}

export function appendBusinessIdToFormData(
  formData,
  { documentCategory, clientBusinessId } = {},
) {
  const scoped = withBusinessDocumentParams(
    {},
    { documentCategory, clientBusinessId },
  );
  if (scoped.client_business_id) {
    formData.append("client_business_id", scoped.client_business_id);
  }
  return formData;
}

/** Client-side guard when API returns mixed business rows. */
export function recordMatchesBusinessScope(record, clientBusinessId) {
  if (clientBusinessId == null || clientBusinessId === "") return true;
  const recordBusinessId = record?.client_business_id;
  if (recordBusinessId == null || recordBusinessId === "") return true;
  return String(recordBusinessId) === String(clientBusinessId);
}
